import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api/axios";
import { toast } from "react-toastify";
import { getAvatarUrl } from "../../utils/getAssetUrl";
import "./VisitDetail.scss";

/* ───────── helpers ───────── */
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const COLORS = ["#60a5fa", "#34d399", "#fbbf24", "#f87171", "#a78bfa", "#22d3ee", "#f472b6", "#f97316", "#84cc16", "#e879f9", "#38bdf8"];
const hashColorFromId = (id) => {
  if (!id && id !== 0) return COLORS[0];
  let h = 0; const s = String(id);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
};
const fmtDate = (d) => d.toLocaleDateString("tr-TR", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
const fmtTime = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const fmtDateTime = (d) => d.toLocaleString("tr-TR", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
const STATUS_LABEL = {
  PENDING: "Henüz yapılmadı", PLANNED: "Planlandı", COMPLETED: "Yapıldı",
  FAILED: "Yapılamadı", CANCELLED: "İptal edildi", POSTPONED: "Ertelendi",
};
const statusLabel = (s) => STATUS_LABEL[s] || "—";
const statusClass = (s) => `st-${(s || "PLANNED").toLowerCase()}`;
const minutesOf = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 60 + min;
};
const sameDay = (a, b) => a?.toDateString?.() === b?.toDateString?.();
const toDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const timeOptions = Array.from({ length: 96 }, (_, i) => {
  const m = i * 15;
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
});

/* ───────── API ───────── */
async function fetchEventById(id) {
  const { data } = await api.get(`/schedule/events/${id}`);
  return data;
}
async function updateStatus(id, status) {
  try { await api.put(`/schedule/events/${id}`, { status }); return true; }
  catch (e) { toast.error(e?.response?.data?.error || "Durum güncellenemedi"); return false; }
}
async function listStoreVisits(storeId) {
  try { const { data } = await api.get(`/stores/${storeId}/visits`); return Array.isArray(data) ? data : []; }
  catch {
    const { data } = await api.get(`/visits/store/${storeId}`);
    return Array.isArray(data) ? data : [];
  }
}
async function fetchEmployees() {
  try {
    const { data } = await api.get("/employees");
    return Array.isArray(data) ? data : [];
  } catch {
    try {
      const { data } = await api.get("/admin/employees");
      return Array.isArray(data) ? data : [];
    } catch { return []; }
  }
}

/* Event'e en makul visitId'yi bağla */
function pickVisitIdForEvent(event, storeVisits) {
  if (!event?.start || !Array.isArray(storeVisits)) return null;
  const evStart = new Date(event.start);
  const evEnd = new Date(event.end);
  const evSMin = evStart.getHours() * 60 + evStart.getMinutes();
  const evEMin = evEnd.getHours() * 60 + evEnd.getMinutes();

  const sameDayVisits = storeVisits.filter(v => {
    const vd = v?.date ? new Date(v.date) : null;
    return vd && sameDay(vd, evStart);
  });

  if (sameDayVisits.length === 0) return null;

  const overlappers = sameDayVisits.filter(v => {
    const s = minutesOf(v?.startTime);
    const e = minutesOf(v?.endTime);
    if (s == null || e == null) return false;
    return (s < evEMin) && (e > evSMin);
  });

  const pick = (arr) => {
    if (arr.length === 0) return null;
    arr.sort((a, b) => {
      const ad = new Date(a.date).getTime();
      const bd = new Date(b.date).getTime();
      return bd - ad;
    });
    return arr[0]?.id ?? null;
  };

  return pick(overlappers) ?? pick(sameDayVisits) ?? null;
}

export default function VisitDetail() {
  const { id: idParam } = useParams();
  const navigate = useNavigate();

  const eventId = useMemo(() => {
    const cleaned = String(idParam ?? "").trim().replace(/^:+/, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }, [idParam]);

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isCustomer = role === "customer";
  // Level 2 kısıtlaması
  const [me, setMe] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile");
        setMe(data);
      } catch { }
    })();
  }, []);

  const isLevel2 = role === "employee" && me?.level === 2;
  const isLevel1 = role === "employee" && me?.level === 1;
  const isPlanner = role === "admin" || (role === "employee" && isLevel1);

  const [event, setEvent] = useState(null);
  const [store, setStore] = useState(null);
  const [visitId, setVisitId] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ── Edit mode state ── */
  const [editing, setEditing] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!eventId) {
          toast.error("Geçersiz etkinlik numarası");
          navigate(-1);
          return;
        }
        setLoading(true);

        const ev = await fetchEventById(eventId);
        ev.start = new Date(ev.start);
        ev.end = new Date(ev.end);
        if (ev.plannedAt) ev.plannedAt = new Date(ev.plannedAt);
        ev.color = ev.color || hashColorFromId(ev.employeeId);
        ev.status = ev.status || "PLANNED";
        setEvent(ev);
        setStore(ev.store || null);

        if (ev.storeId) {
          try {
            const visits = await listStoreVisits(ev.storeId);
            const vId = pickVisitIdForEvent(ev, visits);
            if (vId) setVisitId(vId);
          } catch { }
        }
      } catch (e) {
        const st = e?.response?.status;
        if (st === 400) toast.error("Geçersiz etkinlik numarası");
        else if (st === 403) toast.error("Bu ziyareti görüntüleme yetkiniz yok");
        else if (st === 404) toast.error("Ziyaret bulunamadı");
        else toast.error(e?.response?.data?.error || e.message || "Detaylar getirilemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, navigate]);

  /* Load employees when edit mode is activated */
  useEffect(() => {
    if (editing && employees.length === 0) {
      fetchEmployees().then(setEmployees);
    }
  }, [editing, employees.length]);

  /* Populate edit form from current event */
  const startEdit = () => {
    if (!event) return;
    setEditData({
      employeeId: event.employeeId || "",
      date: toDateInput(event.start),
      startTime: fmtTime(event.start),
      endTime: fmtTime(event.end),
      notes: event.notes || "",
      title: event.title || "",
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditData({});
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!event || !editData.date || !editData.startTime || !editData.endTime) {
      toast.error("Tarih ve saat alanları boş bırakılamaz.");
      return;
    }

    const [sh, sm] = editData.startTime.split(":").map(Number);
    const [eh, em] = editData.endTime.split(":").map(Number);
    const [y, m, d] = editData.date.split("-").map(Number);

    const newStart = new Date(y, m - 1, d, sh, sm, 0, 0);
    const newEnd = new Date(y, m - 1, d, eh, em, 0, 0);

    if (newEnd <= newStart) {
      toast.error("Bitiş saati başlangıçtan sonra olmalıdır.");
      return;
    }

    const body = {};
    if (Number(editData.employeeId) !== event.employeeId) body.employeeId = Number(editData.employeeId);
    if (newStart.getTime() !== event.start.getTime()) body.start = newStart.toISOString();
    if (newEnd.getTime() !== event.end.getTime()) body.end = newEnd.toISOString();
    if (editData.notes !== (event.notes || "")) body.notes = editData.notes;
    if (editData.title !== (event.title || "")) body.title = editData.title;

    if (Object.keys(body).length === 0) {
      toast.info("Değişiklik yapılmadı.");
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      await api.put(`/schedule/events/${event.id}`, body);
      toast.success("Ziyaret başarıyla güncellendi.");
      // Reload event
      const ev = await fetchEventById(event.id);
      ev.start = new Date(ev.start);
      ev.end = new Date(ev.end);
      if (ev.plannedAt) ev.plannedAt = new Date(ev.plannedAt);
      ev.color = ev.color || hashColorFromId(ev.employeeId);
      ev.status = ev.status || "PLANNED";
      setEvent(ev);
      setStore(ev.store || null);
      setEditing(false);
    } catch (err) {
      const msg = err?.response?.data?.error || "Güncelleme başarısız oldu.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* Görüntülenecek personel adı ve avatarı */
  const employeeDisplay = useMemo(() => {
    if (!event) return "-";
    return (
      event.employee?.fullName ||
      event.employeeName ||
      event.employee?.name ||
      event.employee?.email ||
      (event.employeeId ? `Personel #${event.employeeId}` : "-")
    );
  }, [event]);

  const employeeAvatar = useMemo(() => {
    const raw = event?.employeeAvatar || event?.employee?.profileImage || null;
    const url = getAvatarUrl(raw);
    return url || "/noavatar.jpg";
  }, [event]);

  const plannedByDisplay = useMemo(() => event?.plannedByName || "-", [event]);
  const durationMin = useMemo(() => (event ? Math.round((event.end - event.start) / 60000) : 0), [event]);

  const mapSrc = useMemo(() => {
    if (!store) return null;
    const { address, city, name } = store;
    const q = encodeURIComponent(address || city || name || "");
    return q ? `https://maps.google.com/maps?q=${q}&z=15&output=embed` : null;
  }, [store]);

  const directionsUrl = useMemo(() => {
    if (!store) return null;
    const { address, city, name } = store;
    const q = encodeURIComponent(address || `${name || ""} ${city || ""}`.trim());
    return q ? `https://www.google.com/maps/dir/?api=1&destination=${q}` : null;
  }, [store]);

  if (loading) {
    return (
      <Layout title="Ziyaret Detayı">
        <div className="vd-page"><div className="card">Yükleniyor…</div></div>
      </Layout>
    );
  }
  if (!event) {
    return (
      <Layout title="Ziyaret Detayı">
        <div className="vd-page"><div className="card">Ziyaret bulunamadı.</div></div>
      </Layout>
    );
  }

  const canViewEk1 = event?.status === "COMPLETED" && !!visitId;
  const ek1PreviewPath = visitId ? `/ek1/visit/${visitId}` : null;

  const storeDetailPath = store?.id
    ? (isCustomer ? `/customer/stores/${store.id}` : `/admin/stores/${store.id}`)
    : null;

  return (
    <Layout title="Ziyaret Detayı">
      <div className="vd-page">
        {/* Başlık */}
        <div className="vd-head card">
          <div className="title">
            <span className="dot" style={{ background: event.color }} />
            <h1>{event.title || "Ziyaret"}</h1>
          </div>
          <div className="actions">
            {storeDetailPath && <Link className="btn ghost" to={storeDetailPath}>Mağaza Detayı</Link>}

            {/* Admin/Employee edit button */}
            {isPlanner && !editing && (
              <button className="btn ghost" onClick={startEdit}>✏️ Düzenle</button>
            )}

            {/* Admin/Employee */}
            {!isCustomer && canViewEk1 && ek1PreviewPath && (
              <Link to={ek1PreviewPath} className="btn">EK-1 Görüntüle</Link>
            )}
            {!isCustomer && store?.id && (
              <Link to={`/admin/stores/${store.id}/ek1`} className="btn">EK-1 Rapor Doldur</Link>
            )}

            {/* Müşteri */}
            {isCustomer && canViewEk1 && ek1PreviewPath && (
              <Link to={ek1PreviewPath} className="btn">EK-1 Görüntüle</Link>
            )}
          </div>
        </div>

        {/* ── EDIT FORM ── */}
        {editing && (
          <div className="card vd-edit-form">
            <div className="sec-title">Ziyareti Düzenle</div>

            <div className="edit-grid">
              <div className="edit-field">
                <label>Ziyaret Başlığı</label>
                <input
                  type="text"
                  value={editData.title || ""}
                  onChange={e => handleEditChange("title", e.target.value)}
                />
              </div>

              <div className="edit-field">
                <label>Uygulayıcı Personel</label>
                <select
                  value={editData.employeeId || ""}
                  onChange={e => handleEditChange("employeeId", e.target.value)}
                >
                  <option value="">Seçin…</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.fullName || emp.name || emp.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="edit-field">
                <label>Tarih</label>
                <input
                  type="date"
                  value={editData.date || ""}
                  onChange={e => handleEditChange("date", e.target.value)}
                />
              </div>

              <div className="edit-field">
                <label>Başlangıç</label>
                <select
                  value={editData.startTime || ""}
                  onChange={e => handleEditChange("startTime", e.target.value)}
                >
                  {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="edit-field">
                <label>Bitiş</label>
                <select
                  value={editData.endTime || ""}
                  onChange={e => handleEditChange("endTime", e.target.value)}
                >
                  {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="edit-field full">
                <label>Notlar</label>
                <textarea
                  rows={3}
                  value={editData.notes || ""}
                  onChange={e => handleEditChange("notes", e.target.value)}
                  placeholder="(Opsiyonel) Not ekleyin…"
                />
              </div>
            </div>

            <div className="edit-actions">
              <button className="btn ghost" onClick={cancelEdit} disabled={saving}>Vazgeç</button>
              <button className="btn" onClick={handleSave} disabled={saving}>
                {saving ? "Kaydediliyor…" : "Değişiklikleri Kaydet"}
              </button>
            </div>
          </div>
        )}

        {/* Bilgiler */}
        <div className="vd-grid">
          {/* Ziyaret Bilgisi */}
          <div className="card info">
            <div className="sec-title">Ziyaret Bilgisi</div>

            <div className="kv"><div className="k">Tarih</div><div className="v">{fmtDate(event.start)}</div></div>
            <div className="kv"><div className="k">Saat</div><div className="v">{fmtTime(event.start)} – {fmtTime(event.end)} ({durationMin} dk)</div></div>

            {/* Planlayan bilgisi müşteri ve Level 2 görmesin */}
            {(!isCustomer && !isLevel2) && (
              <>
                <div className="kv"><div className="k">Planlayan</div><div className="v">{plannedByDisplay}</div></div>
                <div className="kv"><div className="k">Planlama Zamanı</div><div className="v">{event.plannedAt ? fmtDateTime(event.plannedAt) : "-"}</div></div>
              </>
            )}

            {event.storeName && <div className="kv"><div className="k">Mağaza</div><div className="v">{event.storeName}</div></div>}
            {event.notes && <div className="kv"><div className="k">Notlar</div><div className="v">{event.notes}</div></div>}
            <div className="kv">
              <div className="k">Durum</div>
              <div className="v">
                <span className={`badge ${statusClass(event.status)}`}>{statusLabel(event.status)}</span>
                {isCustomer && (event?.status === "PLANNED" || event?.status === "COMPLETED") && (
                  <div style={{ marginTop: "8px" }}>
                    <Link
                      to="/customer/feedback/new"
                      state={{
                        initialStoreId: store?.id,
                        initialTitle: `${store?.name || ""} mağazama yapılan ${fmtDate(event.start)} tarihli ve ${fmtTime(event.start)} saatli ziyarete dair şikayetimdir`
                      }}
                      className="btn ghost xsmall"
                      style={{ fontSize: "0.75rem", padding: "3px 8px" }}
                    >
                      Şikayette Bulun
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Admin/Employee (Level 1) durumu değiştirebilir — müşteri ve Level 2 asla görmez */}
            {(!isCustomer && !isLevel2) && (
              <div className="kv">
                <div className="k">Durumu Değiştir</div>
                <div className="v">
                  <div className="status-radios">
                    {["PENDING", "PLANNED", "COMPLETED", "FAILED", "CANCELLED", "POSTPONED"].map(s => (
                      <label key={s} className={`radio ${statusClass(s)}`}>
                        <input
                          type="radio"
                          name="visit-status"
                          value={s}
                          checked={event.status === s}
                          disabled={isCustomer}
                          onChange={async () => {
                            if (isCustomer) return;
                            const ok = await updateStatus(event.id, s);
                            if (ok) {
                              setEvent(prev => ({ ...prev, status: s }));
                              toast.success("Durum güncellendi");
                            }
                          }}
                        />
                        <span>{statusLabel(s)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mağaza Bilgisi */}
          <div className="card info">
            <div className="sec-title">Mağaza Bilgisi</div>
            {store ? (
              <>
                <div className="kv"><div className="k">Adı</div><div className="v">{store.code ? `${store.code} – ` : ""}{store.name}</div></div>
                {store.city && <div className="kv"><div className="k">Şehir</div><div className="v">{store.city}</div></div>}
                {store.address && <div className="kv"><div className="k">Adres</div><div className="v">{store.address}</div></div>}
                {(store.manager || store.phone) && (
                  <div className="kv"><div className="k">İrtibat</div><div className="v">{store.manager || "-"}{store.phone ? ` · ${store.phone}` : ""}</div></div>
                )}
                <div className="kv"><div className="k">Durum</div><div className="v">{store.isActive ? "Aktif" : "Pasif"}</div></div>
              </>
            ) : <div>Mağaza bilgisi bulunamadı.</div>}
          </div>

          {/* Personel Kartı */}
          <div className="card person-card">
            <div className="sec-title">Personel</div>
            <div className="person">
              <img className="avatar" src={employeeAvatar} alt="Personel" />
              <div className="meta">
                <div className="label">Uygulayıcı personel:</div>
                <div className="name">{employeeDisplay}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Harita */}
        <div className="card map-card">
          <div className="sec-title row">
            <span>Konum</span>
            {directionsUrl && <a className="btn ghost" href={directionsUrl} target="_blank" rel="noopener noreferrer">Yol Tarifi</a>}
          </div>
          {mapSrc ? (
            <div className="map-wrap">
              <iframe title="store-map" src={mapSrc} width="100%" height={420} style={{ border: 0, borderRadius: 10 }} allowFullScreen loading="lazy" />
            </div>
          ) : <div>Konum bilgisi bulunamadı.</div>}
        </div>

        {/* Alt */}
        <div className="vd-foot">
          <button className="btn ghost" onClick={() => navigate(-1)}>Geri</button>
          {storeDetailPath && <Link className="btn ghost" to={storeDetailPath}>Mağaza Detayı</Link>}
          {!isCustomer && canViewEk1 && ek1PreviewPath && <Link className="btn" to={ek1PreviewPath}>EK-1 Görüntüle</Link>}
          {!isCustomer && store?.id && <Link className="btn" to={`/admin/stores/${store.id}/ek1`}>EK-1 Rapor Doldur</Link>}
          {isCustomer && canViewEk1 && ek1PreviewPath && <Link className="btn" to={ek1PreviewPath}>EK-1 Görüntüle</Link>}
        </div>
      </div>
    </Layout>
  );
}
